import './OmniGallery.module.less';

import {
  File,
  FileObject,
  FileTranscript,
  type Memo,
  NoteTranslation,
  URLObject,
} from '@kalos/kalos-rpc';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  LoadingIcon,
  Textarea,
  toast,
  useAlert,
} from '@kalos/ui';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { CopyIcon, Pencil1Icon, PlayIcon, TrashIcon } from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react';

import { queryKeys } from '../../../hooks/react-query/constants';
import { useRemoveFileFromMemoMutation } from '../../../hooks/react-query/useMemoQuery';
import { useTranscriptTranslationsQueries } from '../../../hooks/react-query/useNoteTranslationQuery';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import {
  convertPCMToWAV,
  FileTranscriptClientService,
  getMimeType,
  NoteTranslationClientService,
  S3ClientService,
} from '../../../tools/helpers';
import { AudioNotePlayer } from './components/AudioNotePlayer';
import { ImageGallery } from './components/ImageGallery';

// --------------------------------------------------
// Types shared across the component
// --------------------------------------------------
type DownloadState = 'idle' | 'downloading' | 'ready' | 'error';

interface VideoEntry {
  meta: File; // protobuf meta
  object?: FileObject; // populated once downloaded
  state: DownloadState;
}

const getLanguageDisplayName = (langCode: string): string => {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
    return displayNames.of(langCode) ?? langCode;
  } catch {
    return langCode;
  }
};

const formatTranslationStatus = (status: string): string =>
  status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());

interface Props {
  files: File[];
  showVideos?: boolean;
  title: string;
  /**
   * Optional header text to render above the gallery. If not provided, nothing is rendered.
   */
  header?: string | ReactNode;
  /**
   * Optional sub-header text to render under the header (and above the gallery).
   */
  subheader?: string | ReactNode;
  readOnly?: boolean;
  allowDelete?: boolean;
  memoId?: number;
  fileToMemoMap?: Map<number, Memo>;
  /**
   * Optional render function to display custom overlay on each photo
   */
  renderPhotoOverlay?: (file: File) => ReactNode;
  /**
   * Optional render function to display custom header section above the photo
   */
  renderPhotoHeader?: (file: File) => ReactNode;
}

interface state$ {
  loading: boolean;
  loaded: boolean;
  files: File[];
  videos: VideoEntry[];
  audioFiles: FileObject[];
  galleryFiles: File[];
  openImageModal: boolean;
  file: FileObject;
  fileType: string;
  fileTranscript: string;
  fileSummary: string;
  editedTranscript: string;
  editTranscript: boolean;
  saving: boolean;
  convertedDataCache: { [key: string]: string };
  processingFiles: number;
  totalFiles: number;
  editingAudioId: number | null;
  videoDialogLoading: boolean;
  selectedVideoIndex: number | null;
  editingTranslationId: number | null;
  editedTranslationText: string;
}

export const OmniGallery: FC<Props> = ({
  files,
  title,
  showVideos = false,
  header,
  subheader,
  readOnly,
  allowDelete = false,
  memoId,
  fileToMemoMap,
  renderPhotoOverlay,
  renderPhotoHeader,
}) => {
  // --------------------------------------------------
  // Helpers (component scope)
  // --------------------------------------------------
  const initialState = {
    loading: true,
    loaded: false,
    files: files,
    videos: [],
    audioFiles: [],
    galleryFiles: [],
    openImageModal: false,
    file: FileObject.create(),
    fileType: '',
    fileTranscript: '',
    fileSummary: '',
    editedTranscript: '',
    editTranscript: false,
    saving: false,
    convertedDataCache: {},
    processingFiles: 0,
    totalFiles: 0,
    editingAudioId: null,
    videoDialogLoading: false,
    selectedVideoIndex: null,
    editingTranslationId: null,
    editedTranslationText: '',
  };

  const [state, setState] = useState<state$>(initialState);

  const [, copyToClipboard] = useCopyToClipboard();
  const { mutateAsync: removeFile } = useRemoveFileFromMemoMutation();
  const queryClient = useQueryClient();

  const handleSetState = useCallback(function handleSetState(partialState: Partial<state$>) {
    setState((currState) =>
      produce(currState, (draft) => {
        return { ...draft, ...partialState };
      }),
    );
  }, []);

  const audioFileIds = state.audioFiles.map((a) => a.id).filter(Boolean) as number[];
  const { translationsByFileId } = useTranscriptTranslationsQueries({
    fileIds: audioFileIds,
    targetLanguage: 'en',
    enabled: state.loaded && audioFileIds.length > 0,
  });

  // Reset video dialog state when modal closes
  const handleModalClose = useCallback(() => {
    handleSetState({
      openImageModal: false,
      file: FileObject.create(),
      fileType: '',
      fileTranscript: '',
      editTranscript: false,
      videoDialogLoading: false,
      selectedVideoIndex: null,
    });
  }, [handleSetState]);

  // --------------------------------------------------
  // Utility to download a file from S3 and return FileObject
  // --------------------------------------------------
  const downloadFile = async (url: URLObject) => {
    const urlRes = await S3ClientService.GetDownloadURL(url);
    const downloadRes = await fetch(urlRes!.url);
    const res = FileObject.create();
    res.key = `${url.key}`;
    res.bucket = url.bucket;
    res.mimeType = getMimeType(url.key);
    if (downloadRes.status === 200) {
      const u8 = new Uint8Array(await downloadRes.arrayBuffer());
      res.data = u8;
      res.uri = downloadRes.url;
    }
    return res;
  };
  const convertData = (file: FileObject) => {
    const mimeType = file.mimeType;
    const uint8Array = file.data;
    const cacheKey = `${file.bucket}:${file.key}`;
    const cached = state.convertedDataCache[cacheKey];
    if (cached) {
      return cached;
    }

    // For PCM audio, use our helper function
    if (mimeType === 'audio/pcm') {
      // Convert uint8Array to base64 string
      let binaryString = '';
      for (let i = 0; i < uint8Array.length; i++) {
        binaryString += String.fromCharCode(uint8Array[i]);
      }
      const base64String = btoa(binaryString);
      const dataUrl = `data:${mimeType};base64,${base64String}`;

      // Use the shared helper function to convert PCM to WAV
      const convertedData = convertPCMToWAV(mimeType, dataUrl);

      handleSetState({
        convertedDataCache: { ...state.convertedDataCache, [cacheKey]: convertedData },
      });
      return convertedData;
    }

    // For other formats (video, etc.), convert to data URL as before
    let binaryString = '';
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }
    const base64String = btoa(binaryString);
    const convertedData = `data:${mimeType};base64,${base64String}`;

    handleSetState({
      convertedDataCache: { ...state.convertedDataCache, [cacheKey]: convertedData },
    });
    return convertedData;
  };

  const getFiles = useCallback(async () => {
    const videos: VideoEntry[] = [];
    const audioFiles: FileObject[] = [];
    const galleryFiles: File[] = [];
    // Begin loading while we download audio/video files that require network requests
    handleSetState({ loading: true });

    const promises = files.map(async (file: File) => {
      const mimeType = file.mimeType != '' ? file.mimeType : getMimeType(file.name);
      const req = URLObject.create({
        key: file.name,
        bucket: file.bucket,
        contentType: mimeType,
      });

      try {
        if (mimeType.includes('pdf')) {
          galleryFiles.push(file);
        } else if (mimeType.includes('audio')) {
          const res = await downloadFile(req);
          res.id = file.id;
          if (res) {
            res.mimeType = mimeType;
            audioFiles.push(res);
          }
        } else if (mimeType.includes('video')) {
          // Create a placeholder entry – download happens lazily on play
          videos.push({ meta: file, state: 'idle' });
        } else if (mimeType.includes('image')) {
          galleryFiles.push(file);
        } else {
          // Unknown or other file types – let subcomponents handle them as needed
          galleryFiles.push(file);
        }
      } catch (error) {
        console.error('Failed to download file:', error);
      } finally {
        // Update processing count - we'll update this differently since handleSetState expects partial state
        // We'll track this at the end of the Promise.all instead
      }
    });

    await Promise.all(promises);

    // Ensure consistent ordering by id
    videos.sort((a, b) => (a.meta.id ?? 0) - (b.meta.id ?? 0));
    galleryFiles.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    audioFiles.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

    handleSetState({
      loading: false,
      loaded: true,
      videos,
      audioFiles,
      galleryFiles,
      files: files,
      processingFiles: 0,
    });
  }, [files, handleSetState]);

  // --------------------------------------------------
  // Lazy URL resolution for videos (like VideoGallery)
  // --------------------------------------------------
  const ensureVideoUrl = async (index: number) => {
    const entry = state.videos[index];
    if (!entry || entry.state === 'downloading' || entry.state === 'ready') return;

    // Optimistically mark as downloading
    handleSetState({
      videos: state.videos.map((v, i) => (i === index ? { ...v, state: 'downloading' } : v)),
    });

    const { meta } = entry;
    const mimeType = meta.mimeType !== '' ? meta.mimeType : getMimeType(meta.name);

    const req = URLObject.create({
      key: meta.name,
      bucket: meta.bucket,
      contentType: mimeType,
    });

    try {
      const urlRes = await S3ClientService.GetDownloadURL(req);
      if (!urlRes?.url) {
        throw new Error('Failed to get download URL');
      }

      // Create a minimal FileObject with just the URL
      const res = FileObject.create();
      res.id = meta.id;
      res.key = meta.name;
      res.bucket = meta.bucket;
      res.mimeType = mimeType;
      res.uri = urlRes.url; // Store the signed URL instead of binary data

      handleSetState({
        videos: state.videos.map((v, i) =>
          i === index ? { ...v, object: res, state: 'ready' } : v,
        ),
      });

      // If this video was selected for dialog, update the loading state
      handleSetState({
        videoDialogLoading: false,
      });
    } catch (error) {
      console.error('Failed to get video URL', error);
      toast({ title: 'Error loading video', variant: 'destructive' });
      handleSetState({
        videos: state.videos.map((v, i) => (i === index ? { ...v, state: 'error' } : v)),
        videoDialogLoading: false,
      });
    }
  };

  const alert = useAlert();

  // Effect to manage video dialog loading state
  useEffect(() => {
    if (state.selectedVideoIndex !== null && state.videos[state.selectedVideoIndex]) {
      const selectedVideo = state.videos[state.selectedVideoIndex];
      if (selectedVideo.state === 'ready' && state.videoDialogLoading) {
        handleSetState({ videoDialogLoading: false });
      }
    }
  }, [state.videos, state.selectedVideoIndex, state.videoDialogLoading, handleSetState]);

  // Fallback timeout to prevent infinite loading
  useEffect(() => {
    if (state.videoDialogLoading) {
      const timeout = setTimeout(() => {
        handleSetState({ videoDialogLoading: false });
      }, 30000); // 30 second timeout

      return () => clearTimeout(timeout);
    }
  }, [state.videoDialogLoading, handleSetState]);

  const [isSaving, setIsSaving] = useState(false);
  const handleSaveTranscript = async (audio?: FileObject) => {
    setIsSaving(true);
    const fileTargetId = audio ? audio.id : state.file.id;
    const file = File.clone(state.files.find((f) => f.id === fileTargetId)!);
    const updatedFileArray = [...state.files];
    const fileTranscriptReq = FileTranscript.create();
    fileTranscriptReq.fileId = file.id;
    try {
      const res = await FileTranscriptClientService.Get(fileTranscriptReq);
      fileTranscriptReq.fileTranscript = state.editedTranscript;
      fileTranscriptReq.fieldMask = ['FileTranscript'];
      if (res) {
        fileTranscriptReq.fileTranscriptId = res.fileTranscriptId;
        await FileTranscriptClientService.Update(fileTranscriptReq);
        file.fileTranscript = state.editedTranscript;
        updatedFileArray.splice(
          state.files.findIndex((f) => f.id === file.id),
          1,
        );
        updatedFileArray.push(file);
      }
      alert('Summary will be updated soon!');
      handleSetState({ editingAudioId: null, editedTranscript: '' });
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }

    handleSetState({ files: updatedFileArray });
  };

  const handleDeleteFile = useCallback(
    async (file: File) => {
      if (!memoId || !file.id) {
        toast({
          title: 'Error',
          description: 'Missing memo ID or file ID',
          variant: 'destructive',
        });
        return;
      }

      try {
        await removeFile({
          memoId,
          fileId: file.id,
        });

        // Update local state for immediate UI update
        handleSetState({
          galleryFiles: state.galleryFiles.filter((f) => f.id !== file.id),
          files: state.files.filter((f) => f.id !== file.id),
          openImageModal: false,
          file: FileObject.create(),
          fileType: '',
          fileTranscript: '',
          fileSummary: '',
          editedTranscript: '',
          editTranscript: false,
        });

        // Update all memo lists to remove the specific file from the memo
        queryClient.setQueriesData(
          { queryKey: [queryKeys.memoList.root] },
          (oldData: { results: any[]; totalCount: number } | undefined) => {
            if (!oldData || !oldData.results) return oldData;

            return {
              ...oldData,
              results: oldData.results.map((memo) => {
                if (memo.id === memoId && memo.files?.results) {
                  return {
                    ...memo,
                    files: {
                      ...memo.files,
                      results: memo.files.results.filter((f: File) => f.id !== file.id),
                    },
                  };
                }
                return memo;
              }),
            };
          },
        );

        // Update the individual memo cache
        queryClient.setQueryData(
          [queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: memoId })],
          (oldData: any) => {
            if (!oldData || !oldData.files?.results) return oldData;

            return {
              ...oldData,
              files: {
                ...oldData.files,
                results: oldData.files.results.filter((f: File) => f.id !== file.id),
              },
            };
          },
        );

        toast({
          title: 'File deleted',
          description: 'The file has been removed successfully',
        });
      } catch (error) {
        console.error('Error deleting file:', error);
        toast({
          title: 'Error deleting file',
          description: 'There was a problem removing the file',
          variant: 'destructive',
        });
      }
    },
    [memoId, removeFile, handleSetState, state.galleryFiles, state.files, queryClient],
  );

  // Add effect to cleanup when component unmounts or modal closes
  useEffect(() => {
    if (!state.openImageModal) {
      handleSetState({
        file: FileObject.create(),
        fileType: '',
        fileTranscript: '',
        fileSummary: '',
        editedTranscript: '',
        editTranscript: false,
      });
    }
  }, [state.openImageModal, handleSetState, state.galleryFiles.length, state.files.length]);

  const renderDeleteAction = useCallback(
    ({ original }: { original: File }) => {
      if (!allowDelete || readOnly || !memoId) return null;

      return (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="icon" variant="destructive">
              <TrashIcon className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete File</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this file? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => handleDeleteFile(original)}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    },
    [allowDelete, readOnly, memoId, handleDeleteFile],
  );

  const handleSaveTranslation = async (translation: NoteTranslation) => {
    setIsSaving(true);
    try {
      const updateReq = NoteTranslation.create();
      updateReq.noteTranslationId = translation.noteTranslationId;
      updateReq.translationText = state.editedTranslationText;
      updateReq.fieldMask = ['translation_text'];
      await NoteTranslationClientService.Update(updateReq);

      handleSetState({ editingTranslationId: null, editedTranslationText: '' });
      queryClient.invalidateQueries({ queryKey: [queryKeys.noteTranslation.root] });
      toast({ title: 'Translation updated' });
    } catch (err) {
      console.error('Failed to update translation:', err);
      toast({
        title: 'Error updating translation',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTranslation = async (translation: NoteTranslation) => {
    try {
      await NoteTranslationClientService.Delete(translation.noteTranslationId);
      queryClient.invalidateQueries({ queryKey: [queryKeys.noteTranslation.root] });
      toast({ title: 'Translation deleted' });
    } catch (err) {
      console.error('Failed to delete translation:', err);
      toast({
        title: 'Error deleting translation',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (!state.loaded) {
      getFiles();
    }
  }, [getFiles, state.loaded]);

  return (
    <>
      {state.loading ? (
        <div className="Omni-Gallery-Loader flex flex-col items-center justify-center">
          <LoadingIcon className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="mx-auto w-full">
          {/* Optional header/subheader section */}
          {(header || subheader) && (
            <div className="mb-4 text-center">
              {header && <h2 className="text-xl font-semibold leading-tight">{header}</h2>}
              {subheader && <p className="text-muted-foreground mt-1 text-sm">{subheader}</p>}
            </div>
          )}
          {/* Existing gallery sections */}
          {state.galleryFiles.length > 0 && (
            <div className="mb-8 h-[80vh] w-full">
              <ImageGallery
                files={state.galleryFiles}
                title={title}
                actions={allowDelete ? renderDeleteAction : undefined}
                renderOverlay={renderPhotoOverlay}
                renderHeader={renderPhotoHeader}
              />
            </div>
          )}
          {state.audioFiles && state.audioFiles.length > 0 && (
            <Accordion type="single" collapsible className="mb-6">
              <AccordionItem value="audio" className="border-b-0">
                <AccordionTrigger className="border-input hover:bg-primary/5 rounded-lg border bg-white px-4 py-3">
                  Audio Files ({state.audioFiles.length})
                </AccordionTrigger>
                <AccordionContent>
                  <div className="w-full p-4">
                    {state.audioFiles.map((audio, index) => (
                      <div key={index} className="mb-4 w-full">
                        <Card className="border-input w-full border bg-white transition-all hover:shadow-md">
                          <CardContent className="flex flex-col gap-2 p-4">
                            {(() => {
                              const meta = state.files.find((f) => f.id === audio.id);
                              const transcript = meta?.fileTranscript || '';
                              const summary = meta?.fileSummary || '';
                              const isEditing = state.editingAudioId === audio.id;
                              const translations = translationsByFileId[audio.id] ?? [];

                              return (
                                <AudioNotePlayer
                                  src={convertData(audio)}
                                  mimeType={audio.mimeType}
                                  fileName={audio.key}
                                  transcript={transcript}
                                  onCopyTranscript={() => copyToClipboard(transcript)}
                                >
                                  {/* Transcript Section */}
                                  <div className="mb-3">
                                    <div className="mb-2 flex items-center justify-between">
                                      <Typography
                                        variant="subtitle2"
                                        component="div"
                                        sx={{ fontWeight: 'bold', color: '#4B5563' }}
                                      >
                                        Transcript
                                      </Typography>
                                      <div className="flex items-center gap-1">
                                        {!readOnly && transcript && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 text-xs"
                                            onClick={() =>
                                              handleSetState({
                                                editingAudioId: audio.id,
                                                editedTranscript: transcript,
                                              })
                                            }
                                          >
                                            Edit
                                          </Button>
                                        )}
                                      </div>
                                    </div>

                                    {isEditing ? (
                                      <>
                                        <Textarea
                                          rows={6}
                                          value={state.editedTranscript}
                                          onChange={(e) =>
                                            handleSetState({ editedTranscript: e.target.value })
                                          }
                                          disabled={readOnly}
                                          className="min-h-[120px] rounded-lg border-gray-200"
                                        />
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            isLoading={isSaving}
                                            onClick={() => handleSaveTranscript(audio)}
                                            disabled={readOnly}
                                          >
                                            Save
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={isSaving}
                                            onClick={() =>
                                              handleSetState({
                                                editingAudioId: null,
                                                editedTranscript: '',
                                              })
                                            }
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </>
                                    ) : transcript ? (
                                      <div className="border-input rounded-lg border bg-white p-3">
                                        <Typography
                                          variant="body2"
                                          className="whitespace-pre-wrap break-words text-gray-700"
                                        >
                                          {transcript}
                                        </Typography>
                                      </div>
                                    ) : (
                                      <p className="text-sm italic text-gray-500">No transcript</p>
                                    )}
                                  </div>

                                  {/* Translations Section - only show when translations exist */}
                                  {transcript && translations.length > 0 && (
                                    <Accordion type="single" collapsible className="my-3 w-full">
                                      <AccordionItem
                                        value="translations"
                                        className="overflow-hidden rounded-lg border border-gray-200 transition-all data-[state=open]:border-indigo-200 data-[state=open]:shadow-md"
                                      >
                                        <AccordionTrigger className="border-input hover:bg-primary/5 rounded-lg border bg-white px-3 py-2 text-sm transition-colors">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-semibold text-gray-600">
                                              Translations
                                            </span>
                                            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                                              {translations.length}
                                            </span>
                                          </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="bg-white">
                                          <div className="divide-y divide-gray-100">
                                            {translations.map((t) => {
                                              const isEditingThis =
                                                state.editingTranslationId === t.noteTranslationId;
                                              return (
                                                <div
                                                  key={t.noteTranslationId}
                                                  className="px-3 py-3"
                                                >
                                                  <div className="mb-2 flex items-center justify-between">
                                                    <span className="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                                                      {getLanguageDisplayName(t.targetLanguage)}
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                      <Button
                                                        size="icon"
                                                        variant="outline"
                                                        className="h-6 w-6"
                                                        onClick={() =>
                                                          copyToClipboard(t.translationText)
                                                        }
                                                        disabled={!t.translationText}
                                                        title="Copy translation"
                                                      >
                                                        <CopyIcon className="h-3 w-3" />
                                                      </Button>
                                                      {!readOnly && t.translationText && (
                                                        <Button
                                                          size="icon"
                                                          variant="outline"
                                                          className="h-6 w-6"
                                                          onClick={() =>
                                                            handleSetState({
                                                              editingTranslationId:
                                                                t.noteTranslationId,
                                                              editedTranslationText:
                                                                t.translationText,
                                                            })
                                                          }
                                                          title="Edit translation"
                                                        >
                                                          <Pencil1Icon className="h-3 w-3" />
                                                        </Button>
                                                      )}
                                                      {!readOnly && (
                                                        <AlertDialog>
                                                          <AlertDialogTrigger asChild>
                                                            <Button
                                                              size="icon"
                                                              variant="outline"
                                                              className="h-6 w-6"
                                                              title="Delete translation"
                                                            >
                                                              <TrashIcon className="h-3 w-3" />
                                                            </Button>
                                                          </AlertDialogTrigger>
                                                          <AlertDialogContent>
                                                            <AlertDialogHeader>
                                                              <AlertDialogTitle>
                                                                Delete Translation
                                                              </AlertDialogTitle>
                                                              <AlertDialogDescription>
                                                                Delete the{' '}
                                                                {getLanguageDisplayName(
                                                                  t.targetLanguage,
                                                                )}{' '}
                                                                translation? This cannot be undone.
                                                              </AlertDialogDescription>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                              <AlertDialogCancel>
                                                                Cancel
                                                              </AlertDialogCancel>
                                                              <AlertDialogAction
                                                                onClick={() =>
                                                                  handleDeleteTranslation(t)
                                                                }
                                                              >
                                                                Delete
                                                              </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                          </AlertDialogContent>
                                                        </AlertDialog>
                                                      )}
                                                    </div>
                                                  </div>
                                                  {isEditingThis ? (
                                                    <>
                                                      <Textarea
                                                        rows={4}
                                                        value={state.editedTranslationText}
                                                        onChange={(e) =>
                                                          handleSetState({
                                                            editedTranslationText: e.target.value,
                                                          })
                                                        }
                                                        disabled={readOnly}
                                                        className="min-h-[80px] rounded-lg border-gray-200"
                                                      />
                                                      <div className="mt-2 flex flex-wrap gap-2">
                                                        <Button
                                                          size="sm"
                                                          variant="outline"
                                                          isLoading={isSaving}
                                                          onClick={() => handleSaveTranslation(t)}
                                                          disabled={readOnly}
                                                        >
                                                          Save
                                                        </Button>
                                                        <Button
                                                          size="sm"
                                                          variant="outline"
                                                          disabled={isSaving}
                                                          onClick={() =>
                                                            handleSetState({
                                                              editingTranslationId: null,
                                                              editedTranslationText: '',
                                                            })
                                                          }
                                                        >
                                                          Cancel
                                                        </Button>
                                                      </div>
                                                    </>
                                                  ) : t.translationText ? (
                                                    <div className="border-input rounded-lg border bg-white p-3">
                                                      <Typography
                                                        variant="body2"
                                                        className="whitespace-pre-wrap break-words text-gray-700"
                                                      >
                                                        {t.translationText}
                                                      </Typography>
                                                    </div>
                                                  ) : (
                                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                                                      <Typography
                                                        variant="body2"
                                                        className="text-sm text-amber-700"
                                                      >
                                                        {t.status === 'PENDING'
                                                          ? 'Translation pending...'
                                                          : t.status === 'IN_PROGRESS'
                                                            ? 'Translation in progress...'
                                                            : 'Translation failed'}
                                                      </Typography>
                                                    </div>
                                                  )}
                                                  {t.status &&
                                                    t.status !== 'COMPLETED' &&
                                                    t.translationText &&
                                                    !isEditingThis && (
                                                      <div className="mt-1.5">
                                                        <span className="text-xs font-medium text-amber-600">
                                                          Status:{' '}
                                                          {formatTranslationStatus(t.status)}
                                                        </span>
                                                      </div>
                                                    )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </AccordionContent>
                                      </AccordionItem>
                                    </Accordion>
                                  )}

                                  {/* Summary Section */}
                                  {summary && (
                                    <div>
                                      <div className="mb-2 flex items-center justify-between">
                                        <Typography
                                          variant="subtitle2"
                                          component="div"
                                          sx={{ fontWeight: 'bold', color: '#4B5563' }}
                                        >
                                          Summary
                                        </Typography>
                                        <Button
                                          size="icon"
                                          variant="outline"
                                          className="h-6 w-6"
                                          onClick={() => copyToClipboard(summary)}
                                        >
                                          <CopyIcon className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                                        <Typography
                                          variant="body2"
                                          className="whitespace-pre-wrap break-words text-gray-700"
                                        >
                                          {summary}
                                        </Typography>
                                      </div>
                                    </div>
                                  )}
                                </AudioNotePlayer>
                              );
                            })()}
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {showVideos && state.videos && state.videos.length > 0 && (
            <Accordion type="single" collapsible className="mb-6">
              <AccordionItem value="videos" className="border-b-0">
                <AccordionTrigger className="border-input hover:bg-primary/5 rounded-lg border bg-white px-4 py-3">
                  Videos ({state.videos.length})
                </AccordionTrigger>
                <AccordionContent>
                  <div className="w-full p-4">
                    <Card className="border-input w-full border bg-white">
                      <CardContent>
                        <div className="w-full">
                          <Grid
                            container
                            spacing={3}
                            direction="row"
                            wrap="nowrap"
                            sx={{
                              overflowX: 'auto',
                              padding: 3,
                              width: '100%',
                              gap: 2,
                              '& .MuiGrid-item': {
                                marginRight: 2,
                                marginBottom: 2,
                              },
                            }}
                          >
                            {state.videos.map((entry, index) => (
                              <Grid
                                item
                                key={entry.meta.id ?? index}
                                sx={{
                                  minWidth: '280px',
                                  flexShrink: 0,
                                  marginRight: 2,
                                }}
                              >
                                <Card
                                  className="border-input m-2 flex w-[280px] min-w-[280px] flex-shrink-0 cursor-pointer flex-col items-center overflow-hidden border bg-white transition-all hover:shadow-lg"
                                  onClick={() => {
                                    // Always set loading state when opening video dialog
                                    const shouldShowLoading = entry.state !== 'ready';
                                    handleSetState({
                                      openImageModal: true,
                                      fileType: 'video',
                                      videoDialogLoading: shouldShowLoading,
                                      selectedVideoIndex: index,
                                    });

                                    // Always start URL resolution if not already ready
                                    if (entry.state !== 'ready') {
                                      ensureVideoUrl(index);
                                    }
                                  }}
                                >
                                  <div className="relative flex aspect-video w-full items-center justify-center bg-gray-100">
                                    {entry.state === 'idle' && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-16 w-16 rounded-full bg-black/20 hover:bg-black/30"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Open dialog and start download
                                          const shouldShowLoading = entry.state !== 'ready';
                                          handleSetState({
                                            openImageModal: true,
                                            fileType: 'video',
                                            videoDialogLoading: shouldShowLoading,
                                            selectedVideoIndex: index,
                                          });
                                          ensureVideoUrl(index);
                                        }}
                                      >
                                        <PlayIcon className="h-8 w-8 text-white" />
                                      </Button>
                                    )}
                                    {entry.state === 'downloading' && (
                                      <div className="flex items-center justify-center">
                                        <LoadingIcon className="h-8 w-8 animate-spin text-gray-400" />
                                      </div>
                                    )}
                                    {entry.state === 'ready' && entry.object && (
                                      <div className="relative h-full w-full">
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity hover:opacity-100">
                                          <PlayIcon className="h-12 w-12 text-white" />
                                        </div>
                                        <div className="flex h-full w-full items-center justify-center bg-gray-100">
                                          <PlayIcon className="h-16 w-16 text-gray-400" />
                                        </div>
                                      </div>
                                    )}
                                    {entry.state === 'error' && (
                                      <div className="flex flex-col items-center gap-2">
                                        <div className="text-sm text-red-500">Download failed</div>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            ensureVideoUrl(index);
                                          }}
                                          className="border-red-300 text-red-600 hover:bg-red-50"
                                        >
                                          Retry
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  <CardContent className="w-full p-3">
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 'medium',
                                        color: '#374151',
                                        fontSize: '0.875rem',
                                        textAlign: 'center',
                                      }}
                                    >
                                      {entry.meta.name}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </div>
      )}
      <Dialog
        open={state.openImageModal}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            handleModalClose();
          }
        }}
      >
        <DialogContent className="flex max-w-4xl flex-col justify-between">
          <DialogHeader className="w-full">
            <DialogTitle>
              {state.fileType === 'video' && state.selectedVideoIndex !== null
                ? state.videos[state.selectedVideoIndex]?.meta.name || 'Video'
                : state.file.key}
            </DialogTitle>
          </DialogHeader>
          {state.fileType === 'video' && (
            <div className="flex w-full flex-auto items-center justify-center">
              <Card className="w-full bg-white">
                {state.videoDialogLoading ? (
                  <div className="flex h-64 items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                      <LoadingIcon className="h-8 w-8 animate-spin text-gray-400" />
                      <Typography variant="body2" color="textSecondary">
                        Loading video...
                      </Typography>
                    </div>
                  </div>
                ) : state.selectedVideoIndex !== null &&
                  state.videos[state.selectedVideoIndex]?.object ? (
                  <video
                    src={state.videos[state.selectedVideoIndex].object!.uri}
                    controls
                    className="h-auto w-full"
                    autoPlay
                  >
                    <source
                      src={state.videos[state.selectedVideoIndex].object!.uri}
                      type={state.videos[state.selectedVideoIndex].object!.mimeType}
                    />
                    Your browser does not support the video element.
                  </video>
                ) : (
                  <div className="flex h-64 items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                      <Typography variant="body2" color="textSecondary">
                        Video not available
                      </Typography>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}
          {state.fileType === 'audio' && (
            <div className="flex w-full flex-auto items-center justify-center">
              <div className="h-[60vh] w-full pt-2.5">
                <div className="flex justify-between gap-4">
                  <div className="flex flex-1 flex-col items-center">
                    <header className="text-center">Transcript</header>
                    <Textarea
                      rows={15}
                      value={state.editedTranscript}
                      onBlur={(e) => {
                        handleSetState({ editedTranscript: e.target.value });
                      }}
                      onChange={(e) => {
                        handleSetState({ editedTranscript: e.target.value });
                      }}
                      style={{
                        width: '100%',
                        height: 'auto',
                        padding: '10px',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        borderColor: '#ccc',
                        resize: 'none',
                        textAlign: 'center',
                      }}
                      disabled={!state.editTranscript}
                    />
                    <div className="mt-4 flex justify-center">
                      {state.editTranscript ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSaveTranscript()}
                          className="mx-2"
                          isLoading={isSaving}
                          disabled={readOnly}
                        >
                          Save
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSetState({ editTranscript: true })}
                          className="mx-2"
                          isLoading={isSaving}
                          disabled={readOnly || isSaving}
                        >
                          Edit
                        </Button>
                      )}

                      {state.editTranscript && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isSaving}
                          onClick={() =>
                            handleSetState({
                              editTranscript: false,
                              editedTranscript: state.fileTranscript,
                            })
                          }
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        onClick={() => copyToClipboard(state.editedTranscript)}
                        className="mx-2"
                        size="sm"
                        variant="outline"
                      >
                        Copy To Clipboard
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col items-center">
                    <header className="text-center">Summary</header>

                    <Textarea
                      rows={15}
                      value={state.fileSummary}
                      style={{
                        height: 'auto',
                        padding: '10px',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        borderColor: '#ccc',
                        resize: 'none',
                        textAlign: 'center',
                      }}
                      disabled={true}
                    />
                    <div className="mt-4 flex justify-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(state.fileSummary)}
                      >
                        Copy To Clipboard
                      </Button>
                    </div>
                  </div>
                </div>
                <audio style={{ width: '100%', marginTop: '10px' }} controls>
                  <source
                    src={convertData(state.file)}
                    type={state.file.mimeType === 'audio/pcm' ? 'audio/wav' : state.file.mimeType}
                  />
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
